<template>
  <form @submit.prevent="submitForm">
    <!-- Title -->
    <div class="mb-6">
      <label class="block text-gray-700 text-sm font-bold mb-2">Название *</label>
      <input
        v-model="form.title"
        type="text"
        required
        class="w-full px-4 py-3 border border-gray-300 rounded-lg text-gray-600 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        placeholder="Введите название объявления"
      />
    </div>

    <!-- Description -->
    <div class="mb-6">
      <label class="block text-gray-700 text-sm font-bold mb-2">Описание *</label>
      <textarea
        v-model="form.description"
        required
        rows="5"
        class="w-full px-4 py-3 border border-gray-300 rounded-lg text-gray-600 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        placeholder="Введите описание объявления"
      />
    </div>

    <!-- Type -->
    <div class="mb-6">
      <label class="block text-gray-700 text-sm font-bold mb-2">Тип объявления *</label>
      <select
        v-model="form.announcement_type"
        required
        class="w-full px-4 py-3 border border-gray-300 rounded-lg text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
      >
        <option value="offer">Предложение услуги (от пилота)</option>
        <option value="request">Запрос на услугу (от работодателя)</option>
      </select>
    </div>

    <!-- Services -->
    <div class="mb-6">
      <label class="block text-gray-700 text-sm font-bold mb-2">Услуги</label>
      <p class="text-gray-500 text-xs mb-3">Выберите требуемые или предлагаемые услуги:</p>
      <div class="space-y-3">
        <div v-for="service in availableServices" :key="service.value" class="flex items-center">
          <input
            :id="`service-${service.value}`"
            type="checkbox"
            :value="service.value"
            v-model="form.services"
            class="w-4 h-4 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 cursor-pointer"
          />
          <label :for="`service-${service.value}`" class="ml-3 text-gray-700 text-sm font-medium cursor-pointer">
            {{ service.label }}
          </label>
        </div>

        <!-- Other Service Option -->
        <div class="flex items-center">
          <input
            id="service-other"
            type="checkbox"
            :value="'__other__'"
            v-model="form.services"
            class="w-4 h-4 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 cursor-pointer"
          />
          <label for="service-other" class="ml-3 text-gray-700 text-sm font-medium cursor-pointer">
            Другое
          </label>
        </div>

        <!-- Custom Service Input -->
        <div v-if="form.services.includes('__other__')" class="ml-8 mt-2">
          <input
            v-model="customServiceName"
            type="text"
            placeholder="Введите название услуги"
            class="w-full px-4 py-2 border border-gray-300 rounded-lg text-gray-600 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
          />
          <p class="text-gray-500 text-xs mt-1">Услуга будет создана и отправлена на подтверждение администратору</p>
        </div>
      </div>
    </div>

    <!-- Budget -->
    <div class="mb-6">
      <label class="block text-gray-700 text-sm font-bold mb-2">Бюджет (KZT)</label>
      <input
        v-model="form.budget"
        type="text"
        class="w-full px-4 py-3 border border-gray-300 rounded-lg text-gray-600 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        placeholder="Например: 50 000 KZT, договорная"
      />
    </div>

    <!-- Location -->
    <div class="mb-6">
      <label class="block text-gray-700 text-sm font-bold mb-2">Место проведения</label>
      <input
        v-model="form.location"
        type="text"
        class="w-full px-4 py-3 border border-gray-300 rounded-lg text-gray-600 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        placeholder="Например: Алматы, Казахстан"
      />
    </div>

    <!-- Contact Info -->
    <div class="mb-6">
      <label class="block text-gray-700 text-sm font-bold mb-2">Контактная информация *</label>
      <input
        v-model="form.contact_info"
        type="text"
        required
        readonly
        class="w-full px-4 py-3 border border-gray-300 rounded-lg text-gray-600 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-100 cursor-not-allowed"
        :placeholder="userPhone || 'Номер телефона не заполнен'"
      />
      <p class="text-gray-500 text-xs mt-1">Номер телефона из вашего профиля. Его нельзя изменить.</p>
    </div>

    <!-- HTML Content Editor -->
    <div class="mb-6">
      <label class="block text-gray-700 text-sm font-bold mb-2">Дополнительный HTML контент</label>
      <p class="text-gray-500 text-xs mb-3">Редактируйте контент с использованием визуального редактора (опционально)</p>
      <QuillEditor
        :content="form.html_content"
        @update:content="(value) => form.html_content = value"
        content-type="html"
        theme="snow"
        class="bg-white"
        style="border-radius: 0.5rem; overflow: hidden;"
      />
    </div>

    <!-- reCAPTCHA v2 -->
    <div class="mb-6 p-4 border border-gray-300 rounded-lg bg-gray-50">
      <div class="g-recaptcha"></div>
    </div>

    <!-- Buttons -->
    <div class="flex gap-4">
      <button
        type="submit"
        :disabled="loading"
        class="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition font-bold text-sm uppercase"
      >
        {{ loading ? 'Создание...' : 'Создать объявление' }}
      </button>
      <button
        type="button"
        @click="$emit('close')"
        class="px-6 py-3 bg-gray-300 text-gray-800 rounded-lg hover:bg-gray-400 transition font-bold text-sm uppercase"
      >
        Отмена
      </button>
    </div>
  </form>
</template>

<script>
import { ref, onMounted } from 'vue';
import axios from 'axios';
import { useStore } from 'vuex';
import { QuillEditor } from '@vueup/vue-quill';
import '@vueup/vue-quill/dist/vue-quill.snow.css';
import { API_PILOT_SERVICES_PUBLIC, API_PILOT_SERVICES_CREATE, API_PROFILE, HEADERS_TOKEN_JSON } from '@/const/uri';

export default {
  name: 'AnnouncementCreateForm',
  components: {
    QuillEditor,
  },
  emits: ['submit', 'close'],
  setup(props, { emit }) {
    const store = useStore();
    const defaultAnnouncementType = store.state.user?.role?.key === 4 ? 'request' : 'offer';
    const form = ref({
      title: '',
      description: '',
      announcement_type: defaultAnnouncementType,
      services: [],
      budget: '',
      location: '',
      contact_info: '',
      html_content: '',
      is_published: true,
    });

    const availableServices = ref([]);
    const loading = ref(false);
    const servicesLoading = ref(false);
    const customServiceName = ref('');
    const userPhone = ref('');

    const fetchUserProfile = async () => {
      try {
        const response = await axios.get(API_PROFILE.list, {
          headers: HEADERS_TOKEN_JSON()
        });

        if (response.data.code === 0 && response.data.data.phone) {
          userPhone.value = response.data.data.phone;
          form.value.contact_info = response.data.data.phone;
        }
      } catch (error) {
        console.error('Error fetching user profile:', error);
      }
    };

    const fetchServices = async () => {
      servicesLoading.value = true;
      try {
        const response = await axios.get(API_PILOT_SERVICES_PUBLIC.list, {
          headers: HEADERS_TOKEN_JSON()
        });

        const services = response.data.data || [];
        availableServices.value = services.map(service => ({
          value: service.key,
          label: service.name
        }));
      } catch (error) {
        console.error('Error fetching pilot services:', error);
        availableServices.value = [
        ];
      } finally {
        servicesLoading.value = false;
      }
    };

    const submitForm = async () => {
      loading.value = true;
      try {
        // Get reCAPTCHA token
        let recaptchaToken = '';
        if (window.grecaptcha) {
          recaptchaToken = window.grecaptcha.getResponse();
        }

        if (!recaptchaToken) {
          alert('Пожалуйста, пройдите проверку reCAPTCHA');
          loading.value = false;
          return;
        }

        // Handle custom service creation
        const services = [...form.value.services];
        if (services.includes('__other__')) {
          if (!customServiceName.value.trim()) {
            alert('Пожалуйста, введите название для новой услуги');
            loading.value = false;
            return;
          }

          try {
            // Create new service
            const response = await axios.post(
              `${API_PILOT_SERVICES_CREATE.create}`,
              { name: customServiceName.value.trim() },
              { headers: HEADERS_TOKEN_JSON() }
            );
            const newService = response.data.data;
            const otherIndex = services.indexOf('__other__');
            if (otherIndex !== -1) {
              services.splice(otherIndex, 1, newService.key);
              form.value.services = services;
              console.log('Updated services:', form.value.services);
            }
          } catch (error) {
            console.error('Error creating service:', error);
            alert('Ошибка при создании услуги. Пожалуйста, попробуйте снова.');
            loading.value = false;
            return;
          }
        }

        const payload = {
          ...form.value,
          recaptcha_token: recaptchaToken,
        };

        emit('submit', payload);
      } catch (error) {
        console.error('Error submitting form:', error);
        alert('Ошибка при отправке формы. Пожалуйста, попробуйте снова.');
        loading.value = false;
      }
    };

    const initRecaptcha = () => {
      const captchaDiv = document.querySelector('.g-recaptcha');
      if (captchaDiv && window.grecaptcha && !captchaDiv.hasChildNodes()) {
        try {
          window.grecaptcha.render(captchaDiv, {
            sitekey: '6LeHugosAAAAAFsEjTbuh8KDsvVbYYp0aUmKxMJE',
          });
        } catch (error) {
          console.error('Error rendering reCAPTCHA:', error);
        }
      }
    };

    onMounted(() => {
      fetchUserProfile();
      fetchServices();

      let attempts = 0;
      const tryRender = setInterval(() => {
        if (window.grecaptcha) {
          initRecaptcha();
          clearInterval(tryRender);
        }
        attempts++;
        if (attempts > 10) {
          clearInterval(tryRender);
          console.warn('Could not load reCAPTCHA after 10 attempts');
        }
      }, 100);
    });

    return {
      form,
      availableServices,
      loading,
      servicesLoading,
      customServiceName,
      userPhone,
      submitForm,
      initRecaptcha,
    };
  },
};
</script>

<style scoped>
/* reCAPTCHA styles */
:deep(.g-recaptcha) {
  display: flex;
  justify-content: center;
}
</style>
